import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormApi } from 'final-form';

import { ConfirmDialog, Typography } from '@occc/ui-design-system';
import { LocationFragment, RetailerNetworksForUserQuery } from '@occc/graphql';
import { useGetLocationsLazyQuery } from '@occc-app/dataAccessLayer/location/queries';

import { RetailerNetworkCustomerAutocomplete } from './retailer-network-customer-autocomplete.component';
import {
  CustomerId,
  RetailerNetworkConfigurationFormValues,
} from './retailer-network-configuration.types';

import { useGetRetailerNetworksForUserQuery } from '../../data-access/queries';
import { routeCreator } from '../retailer-connect.utils';

type RetailerNetworkCustomerAutocompleteContainerProps = {
  dataTestId?: string;
  isRequired?: boolean;
  disabled?: boolean;
  customerId?: CustomerId;
  form: FormApi<RetailerNetworkConfigurationFormValues>;
};

export const RetailerNetworkCustomerAutocompleteContainer = ({
  disabled,
  isRequired,
  dataTestId,
  customerId,
  form,
}: RetailerNetworkCustomerAutocompleteContainerProps) => {
  const navigate = useNavigate();
  const [redirectToEditPageDialogOpen, setRedirectToEditPageDialogOpen] = useState<boolean>(false);

  const onLocationsQueryCompleted = (locations: LocationFragment[]) => {
    const location = locations?.[0];

    if (location?.customerName) {
      form.change('name', location.customerName);
    }
  };

  const { executeQuery: executeLocationsQuery } = useGetLocationsLazyQuery({
    skip: !customerId || disabled,
    onCompleted: onLocationsQueryCompleted,
  });

  const onRetailerNetworksForUserQueryCompleted = (data: RetailerNetworksForUserQuery) => {
    const retailerNetwork = data?.retailerNetworksForUser.edges?.[0]?.node;

    if (retailerNetwork?.uuid) {
      setRedirectToEditPageDialogOpen(true);
    } else {
      executeLocationsQuery({
        variables: {
          filter: {
            customerIdEquals: customerId?.id,
          },
          first: 1,
        },
      });
    }
  };

  const { data } = useGetRetailerNetworksForUserQuery({
    skip: !customerId || disabled,
    variables: {
      filter: {
        customerIdEquals: customerId?.id,
      },
      first: 1,
    },
    onCompleted: onRetailerNetworksForUserQueryCompleted,
  });

  const onRedirectToEditPage = () => {
    const retailerNetwork = data?.retailerNetworksForUser.edges?.[0]?.node;

    if (retailerNetwork) {
      navigate(routeCreator.configurationUpdateNetworkDetails(retailerNetwork?.uuid));
    } else {
      console.error('There is no Retailer Network Uuid to navigate');
    }

    setRedirectToEditPageDialogOpen(false);
  };

  const onDialogClose = () => {
    form.reset();
    form.resetFieldState('name');
    form.resetFieldState('customerId');
    setRedirectToEditPageDialogOpen(false);
  };

  const customerName = `"${customerId?.name}"` || 'this customer';

  return (
    <>
      <RetailerNetworkCustomerAutocomplete
        disabled={disabled}
        isRequired={isRequired}
        dataTestId={dataTestId}
      />
      {redirectToEditPageDialogOpen && (
        <ConfirmDialog
          dialogTitle='Retailer Network already exist.'
          confirmButtonText='Edit Retailer Network'
          open={redirectToEditPageDialogOpen}
          onClose={onDialogClose}
          confirmButtonProps={{
            onClick: onRedirectToEditPage,
          }}
        >
          <Typography sx={{ mt: 1 }}>
            Retailer Network for {customerName} is already exist.
          </Typography>
          <Typography sx={{ mt: 1 }}>
            You can go and edit it, or you can click "Cancel" and pick up another Customer.
          </Typography>
        </ConfirmDialog>
      )}
    </>
  );
};
